import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { subscribeSlowRequests } from '@/lib/slow-requests';

const SHOW_AFTER_MS = 2500;
const STILL_AFTER_MS = 12000;

/**
 * Shown while a server call has been running for a few seconds: the first
 * call after a quiet spell waits for the server to start (usually ~5 s, at
 * worst ~20 s). Disappears as soon as it finishes.
 */
export function SlowRequestNotice() {
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => subscribeSlowRequests(setStartedAt), []);
  useEffect(() => {
    if (startedAt === null) return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [startedAt]);

  const elapsed = startedAt === null ? 0 : now - startedAt;
  if (startedAt === null || elapsed < SHOW_AFTER_MS) return null;
  const seconds = Math.floor(elapsed / 1000);
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="slow-request-notice"
      className="fixed bottom-4 left-1/2 z-[100] flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-3 rounded-full border border-cyan-500/40 bg-gray-900/95 px-4 py-2 text-sm text-white shadow-lg"
    >
      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-cyan-300" />
      <span>
        {elapsed < STILL_AFTER_MS
          ? 'Working on it… the server is starting up, this can take up to 20 seconds.'
          : `Still working (${seconds}s), nearly there.`}
      </span>
    </div>
  );
}
